import { PracticeEnemDay } from '../model/Enem';

/**
 * Lê o `:day` da URL.
 *
 * Devolve `null` para qualquer coisa que não seja 1 ou 2 — inclusive rota sem o
 * parâmetro, que é o simulado de etapa única. O backend trata `null` como prova
 * inteira e recusa números fora da faixa, então nada aqui precisa lançar.
 */
export const parseDayParam = (rawDay?: string): PracticeEnemDay => {
  const day = Number(rawDay);

  return day === 1 || day === 2 ? day : null;
};

/**
 * O backend responde 422 com `requiresDaySelection` quando a chamada não disse
 * o dia e o simulado tem dois. Acontece com links antigos, sem `/dia/N` na URL.
 *
 * O wrapper das queries perde o corpo da resposta ao relançar (`new Error(e)`),
 * então este erro carrega o sinal explicitamente para a tela poder redirecionar
 * em vez de mostrar "conteúdo não encontrado".
 */
export class PracticeEnemDaySelectionRequiredError extends Error {
  readonly requiresDaySelection = true;

  constructor() {
    super('Este simulado tem dois dias. Escolha qual deseja acessar.');
    this.name = 'PracticeEnemDaySelectionRequiredError';
  }
}

export const isDaySelectionRequiredError = (error: unknown) =>
  error instanceof PracticeEnemDaySelectionRequiredError;

export const practiceEnemPaths = {
  list: () => '/simulados/enem',

  daySelection: (slug: string) => `/simulados/enem/${slug}`,

  exam: (slug: string, day: PracticeEnemDay) =>
    day ? `/simulados/enem/${slug}/dia/${day}` : `/simulados/enem/${slug}/prova`,

  feedback: (slug: string, day: PracticeEnemDay) =>
    day
      ? `/simulados/enem/${slug}/dia/${day}/gabarito`
      : `/simulados/enem/${slug}/gabarito`,
};

export const DAY_LABELS: Record<1 | 2, string> = {
  1: 'Linguagens, Códigos e Ciências Humanas',
  2: 'Ciências da Natureza e Matemática',
};

export const DAY_AREAS: Record<1 | 2, string[]> = {
  1: [
    'Língua Portuguesa e Literatura',
    'Língua Estrangeira',
    'História e Geografia',
    'Filosofia e Sociologia',
  ],
  2: ['Biologia', 'Química', 'Física', 'Matemática'],
};

export const formatDayDuration = (durationInMinutes: number | null) => {
  if (!durationInMinutes) return null;

  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;

  if (hours && minutes) return `${hours}h${String(minutes).padStart(2, '0')}`;
  if (hours) return `${hours}h`;

  return `${minutes}min`;
};
